import { Archive, FileSpreadsheet, FileText, ReceiptText } from 'lucide-react';

import { t } from '../lib/i18n';
import { useAppStore } from '../store';

export type ExportFormat = 'csv' | 'xlsx' | 'zip' | 'billcsv';

interface ExportMenuProps {
  /** Number of completed receipts available to export. 0 disables. */
  count: number;
  onExport: (format: ExportFormat) => void;
}

interface ExportButtonProps {
  icon: typeof FileText;
  label: string;
  title?: string;
  disabled: boolean;
  onClick: () => void;
}

function ExportButton({ icon: Icon, label, title, disabled, onClick }: ExportButtonProps) {
  const baseBtn =
    'inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-medium ring-1 ring-inset transition-all';
  const enabled =
    'bg-white text-slate-700 ring-slate-300 shadow-sm hover:bg-slate-50 hover:text-slate-900 hover:shadow cursor-pointer dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-50';
  const off =
    'bg-slate-100 text-slate-400 ring-slate-200 cursor-not-allowed dark:bg-slate-900 dark:text-slate-600 dark:ring-slate-800';
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={title}
      className={`${baseBtn} ${disabled ? off : enabled}`}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {label}
    </button>
  );
}

export function ExportMenu({ count, onExport }: ExportMenuProps) {
  const lang = useAppStore((s) => s.lang);
  const disabled = count === 0;

  const label =
    count === 0
      ? t(lang, 'export.empty')
      : count === 1
        ? t(lang, 'export.one')
        : t(lang, 'export.many', { n: count });

  return (
    <div
      role="group"
      aria-label={t(lang, 'export.regionLabel')}
      className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
    >
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <div className="flex flex-wrap gap-2">
        <ExportButton
          icon={FileText}
          label={t(lang, 'export.csv')}
          disabled={disabled}
          onClick={() => onExport('csv')}
        />
        <ExportButton
          icon={FileSpreadsheet}
          label={t(lang, 'export.xlsx')}
          disabled={disabled}
          onClick={() => onExport('xlsx')}
        />
        <ExportButton
          icon={ReceiptText}
          label={t(lang, 'export.billCsv')}
          title={t(lang, 'export.billCsvTooltip')}
          disabled={disabled}
          onClick={() => onExport('billcsv')}
        />
        <ExportButton
          icon={Archive}
          label={t(lang, 'export.zip')}
          title={t(lang, 'export.zipTooltip')}
          disabled={disabled}
          onClick={() => onExport('zip')}
        />
      </div>
    </div>
  );
}
